import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, UserPlus, Copy, MagnifyingGlass, X, Users, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

export default function CoachClients() {
  const [clients, setClients] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteNote, setInviteNote] = useState("");
  const [inviteMode, setInviteMode] = useState("1on1");
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [tab, setTab] = useState("clients");
  const [rosterSearch, setRosterSearch] = useState("");
  const [rosterSort, setRosterSort] = useState("recent"); // recent | az | newest
  const [rosterFilter, setRosterFilter] = useState("all"); // all | active | silent | new

  // Compute per-filter counts BEFORE search so chip badges reflect the roster,
  // not the current query. Uses fixed windows: active/silent = 7d, new = 14d.
  const filterCounts = useMemo(() => {
    const now = Date.now();
    const WEEK = 7 * 24 * 60 * 60 * 1000;
    const TWO_WEEKS = 14 * 24 * 60 * 60 * 1000;
    let active = 0, silent = 0, isNew = 0;
    for (const link of clients) {
      const lastMs = link.last_workout_date ? Date.parse(link.last_workout_date) : 0;
      const createdMs = link.created_at ? Date.parse(link.created_at) : 0;
      const daysSinceLog = lastMs ? (now - lastMs) : Infinity;
      if (daysSinceLog <= WEEK) active += 1;
      if (daysSinceLog > WEEK) silent += 1;  // includes "never logged"
      if (createdMs && (now - createdMs) <= TWO_WEEKS) isNew += 1;
    }
    return { all: clients.length, active, silent, new: isNew };
  }, [clients]);

  const load = () => {
    Promise.all([api.get("/coach/clients"), api.get("/coach/invites")]).then(([c, i]) => {
      setClients(c.data);
      setInvites(i.data.filter((x) => x.status === "pending"));
    });
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!search.trim()) return setSearchResults([]);
    const t = setTimeout(() => {
      api.get(`/users/search?q=${encodeURIComponent(search)}`).then(({ data }) => setSearchResults(data));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Roster-scoped filter + search + sort. Chip narrows the list; then search
  // matches name/email/goal/notes; then sort orders whatever remains.
  const filteredClients = useMemo(() => {
    const q = rosterSearch.trim().toLowerCase();
    const now = Date.now();
    const WEEK = 7 * 24 * 60 * 60 * 1000;
    const TWO_WEEKS = 14 * 24 * 60 * 60 * 1000;
    let list = clients;
    if (rosterFilter !== "all") {
      list = list.filter((link) => {
        const lastMs = link.last_workout_date ? Date.parse(link.last_workout_date) : 0;
        const createdMs = link.created_at ? Date.parse(link.created_at) : 0;
        const daysSinceLog = lastMs ? (now - lastMs) : Infinity;
        if (rosterFilter === "active") return daysSinceLog <= WEEK;
        if (rosterFilter === "silent") return daysSinceLog > WEEK;
        if (rosterFilter === "new") return createdMs && (now - createdMs) <= TWO_WEEKS;
        return true;
      });
    }
    if (q) {
      list = list.filter((link) => {
        const c = link.client || {};
        return [c.name, c.email, c.primary_goal, link.notes]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      });
    }
    list = [...list];
    if (rosterSort === "az") {
      list.sort((a, b) => (a.client?.name || "").localeCompare(b.client?.name || ""));
    } else if (rosterSort === "newest") {
      list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    } else {
      // recent activity — clients with no logs sink to the bottom
      list.sort((a, b) => (b.last_workout_date || "").localeCompare(a.last_workout_date || ""));
    }
    return list;
  }, [clients, rosterSearch, rosterSort, rosterFilter]);

  const generateLink = async () => {
    try {
      const { data } = await api.post("/coach/invites", { note: inviteNote, mode: inviteMode });
      const link = `${window.location.origin}/join/${data.code}`;
      await navigator.clipboard.writeText(link);
      toast.success("Invite link copied to clipboard");
      setInviteOpen(false);
      setInviteNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const inviteDirect = async (u) => {
    try {
      await api.post("/coach/invites/direct", { target_user_id: u.user_id, note: inviteNote, mode: inviteMode });
      toast.success(`Invite sent to ${u.name}`);
      setSearch("");
      setSearchResults([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const copyInvite = async (code) => {
    const link = `${window.location.origin}/join/${code}`;
    await navigator.clipboard.writeText(link);
    toast.success("Link copied");
  };

  const cancelInvite = async (id) => {
    await api.delete(`/coach/invites/${id}`);
    toast.success("Invite canceled");
    load();
  };

  const removeClient = async (id) => {
    if (!window.confirm("Remove this client from your roster?")) return;
    await api.delete(`/coach/clients/${id}`);
    toast.success("Client removed");
    load();
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="coach-clients-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-tiny text-brand-accent mb-2">// COACH MODE</div>
          <h1 className="font-heading text-4xl sm:text-5xl">CLIENTS</h1>
        </div>
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild>
            <button className="btn-kinetic px-5 py-3 text-xs label-tiny tracking-widest flex items-center gap-2" data-testid="new-invite-button">
              <UserPlus size={14} weight="bold" /> INVITE CLIENT
            </button>
          </DialogTrigger>
          <DialogContent className="bg-surface-DEFAULT border-white/10 text-white max-w-lg" data-testid="invite-dialog">
            <DialogHeader><DialogTitle className="font-heading text-2xl">INVITE A CLIENT</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="label-tiny block mb-2">MODE</label>
                <div className="flex gap-2">
                  {["1on1", "team"].map((m) => (
                    <button key={m} onClick={() => setInviteMode(m)} className={`flex-1 py-2.5 text-xs label-tiny border transition-colors ${inviteMode === m ? "border-brand-accent text-brand-accent" : "border-white/10 text-text-secondary hover:border-white/40"}`} data-testid={`mode-${m}`}>
                      {m === "1on1" ? "1-ON-1" : "TEAM"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label-tiny block mb-2">WELCOME NOTE (OPTIONAL)</label>
                <Textarea value={inviteNote} onChange={(e) => setInviteNote(e.target.value)} placeholder="Excited to work with you..." className="bg-surface-elevated border-white/10" data-testid="invite-note" />
              </div>

              <div className="pt-2 border-t border-white/10">
                <div className="label-tiny mb-3">// OPTION A — INVITE LINK</div>
                <button onClick={generateLink} className="btn-kinetic w-full py-3 text-xs label-tiny tracking-widest flex items-center justify-center gap-2" data-testid="generate-link-button">
                  <LinkSimple size={14} weight="bold" /> GENERATE & COPY LINK
                </button>
              </div>

              <div>
                <div className="label-tiny mb-2">// OPTION B — SEARCH EXISTING USER</div>
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email" className="pl-10 bg-surface-elevated border-white/10" data-testid="user-search" />
                </div>
                {searchResults.length > 0 && (
                  <div className="mt-2 max-h-48 overflow-y-auto border border-white/10">
                    {searchResults.slice(0, 8).map((u) => (
                      <button key={u.user_id} onClick={() => inviteDirect(u)} className="w-full flex items-center justify-between p-2.5 hover:bg-surface-elevated text-left" data-testid={`invite-direct-${u.user_id}`}>
                        <div>
                          <div className="text-sm">{u.name}</div>
                          <div className="text-xs text-text-secondary">{u.email}</div>
                        </div>
                        <UserPlus size={14} className="text-brand-accent" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-1 mb-6 border-b border-white/10">
        <button onClick={() => setTab("clients")} className={`px-4 py-2 text-xs label-tiny border-b-2 transition-colors ${tab === "clients" ? "border-brand-accent text-brand-accent" : "border-transparent text-text-secondary hover:text-white"}`} data-testid="tab-clients">
          ACTIVE CLIENTS ({clients.length})
        </button>
        <button onClick={() => setTab("invites")} className={`px-4 py-2 text-xs label-tiny border-b-2 transition-colors ${tab === "invites" ? "border-brand-accent text-brand-accent" : "border-transparent text-text-secondary hover:text-white"}`} data-testid="tab-invites">
          PENDING INVITES ({invites.length})
        </button>
      </div>

      {tab === "clients" && (
        clients.length === 0 ? (
          <div className="tactical-card p-12 text-center" data-testid="clients-empty">
            <Users size={40} weight="duotone" className="text-brand-primary mx-auto mb-4" />
            <h3 className="font-heading text-xl mb-2">NO CLIENTS YET</h3>
            <p className="text-sm text-text-secondary">Send an invite to add your first client.</p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid="clients-filter-chips">
              {[
                { key: "all",    label: "ALL"              },
                { key: "active", label: "ACTIVE THIS WEEK" },
                { key: "silent", label: "SILENT 7D+"       },
                { key: "new",    label: "NEW"              },
              ].map((chip) => {
                const active = rosterFilter === chip.key;
                const count = filterCounts[chip.key] ?? 0;
                return (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => setRosterFilter(chip.key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] label-tiny tracking-widest border transition-colors ${
                      active
                        ? "!bg-brand-accent text-white !border-brand-accent"
                        : "border-white/20 text-text-secondary hover:!border-brand-accent hover:!text-brand-accent"
                    }`}
                    data-testid={`clients-filter-${chip.key}`}
                    aria-pressed={active}
                  >
                    {chip.label}
                    <span className={`text-[10px] ${active ? "text-white/80" : "text-text-secondary/70"}`}>{count}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="clients-search-sort">
              <div className="relative flex-1 min-w-[180px]">
                <MagnifyingGlass size={13} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary" />
                <input
                  type="text"
                  value={rosterSearch}
                  onChange={(e) => setRosterSearch(e.target.value)}
                  placeholder="Search by name, email, goal, or note..."
                  className="w-full bg-surface-elevated border border-white/15 pl-8 pr-8 py-2 text-sm text-slate-800 placeholder:text-slate-400"
                  data-testid="clients-search-input"
                />
                {rosterSearch && (
                  <button
                    onClick={() => setRosterSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary hover:!text-brand-heat"
                    data-testid="clients-search-clear"
                    aria-label="Clear search"
                  >
                    <X size={12} weight="bold" />
                  </button>
                )}
              </div>
              <select
                value={rosterSort}
                onChange={(e) => setRosterSort(e.target.value)}
                className="bg-surface-elevated border border-white/15 px-3 py-2 text-xs label-tiny tracking-widest text-slate-800 focus:outline-none focus:border-brand-accent"
                data-testid="clients-sort-select"
                aria-label="Sort clients"
              >
                <option value="recent">SORT: RECENT ACTIVITY</option>
                <option value="az">SORT: A → Z</option>
                <option value="newest">SORT: NEWEST ADDED</option>
              </select>
            </div>
            {filteredClients.length === 0 ? (
              <div className="tactical-card p-8 text-center" data-testid="clients-search-empty">
                <p className="text-sm text-text-secondary">
                  {rosterSearch
                    ? <>No clients match &ldquo;<span className="text-brand-accent">{rosterSearch}</span>&rdquo;.</>
                    : <>No clients in <span className="text-brand-accent">{rosterFilter === "silent" ? "SILENT 7D+" : rosterFilter === "active" ? "ACTIVE THIS WEEK" : "NEW"}</span> right now.</>
                  }
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-list">
                {filteredClients.map((link) => (
                  <div key={link.link_id} className="tactical-card p-5" data-testid={`client-${link.client.user_id}`}>
                    <div className="flex items-center gap-3 mb-3">
                      {link.client.picture ? (
                        <img src={link.client.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-brand-primary flex items-center justify-center font-heading">
                          {link.client.name?.[0]?.toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-heading text-base truncate">{link.client.name?.toUpperCase()}</div>
                        <div className="text-xs text-text-secondary truncate">{link.client.email}</div>
                      </div>
                      <span className="label-tiny text-brand-accent">{link.mode?.toUpperCase()}</span>
                    </div>
                    <div className="text-xs text-text-secondary mb-3">
                      {link.last_workout_date ? `Last log: ${new Date(link.last_workout_date).toLocaleDateString()}` : "No workouts yet"}
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/coach/clients/${link.client.user_id}`} className="flex-1" data-testid={`view-client-${link.client.user_id}`}>
                        <button className="w-full btn-kinetic py-2 text-xs label-tiny tracking-widest">VIEW</button>
                      </Link>
                      <button onClick={() => removeClient(link.client.user_id)} className="border border-white/10 hover:border-red-400 hover:text-red-400 px-3 text-text-secondary transition-colors" data-testid={`remove-${link.client.user_id}`}>
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )
      )}

      {tab === "invites" && (
        invites.length === 0 ? (
          <div className="tactical-card p-12 text-center" data-testid="invites-empty">
            <EnvelopeIcon />
            <p className="text-sm text-text-secondary mt-2">No pending invites</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="invites-list">
            {invites.map((inv) => (
              <div key={inv.invite_id} className="tactical-card p-4 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-heading">{inv.email || inv.target_user_id || "OPEN LINK"}</div>
                  <div className="label-tiny mt-1 text-text-secondary">
                    {inv.mode?.toUpperCase()} · CODE {inv.code} · EXPIRES {new Date(inv.expires_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => copyInvite(inv.code)} className="border border-white/10 hover:border-brand-accent p-2 transition-colors" data-testid={`copy-${inv.invite_id}`}>
                    <Copy size={14} />
                  </button>
                  <button onClick={() => cancelInvite(inv.invite_id)} className="border border-white/10 hover:border-red-400 hover:text-red-400 p-2 transition-colors" data-testid={`cancel-${inv.invite_id}`}>
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function EnvelopeIcon() {
  return <div className="w-10 h-10 border border-white/10 mx-auto flex items-center justify-center text-text-secondary">✉</div>;
}
